import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import { type ComponentProps, createContext, use } from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/60 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        'primary-outline':
          'border border-primary bg-background text-primary shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:border-primary dark:bg-input/30 dark:hover:bg-input/50',
        'brand-secondary':
          'bg-brand-secondary text-brand-secondary-foreground hover:bg-brand-secondary/90',
      },
      size: {
        default: 'h-11 px-4 py-2 has-[>svg]:px-3 mobile:h-9',
        sm: 'h-9 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5 mobile:h-8',
        lg: 'h-12 rounded-md px-6 has-[>svg]:px-4 mobile:h-10',
        clear: 'h-auto p-0',
        icon: 'size-11 mobile:size-9',
        'icon-sm': 'size-9 mobile:size-8',
        'icon-lg': 'size-12 mobile:size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type BaseButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants>;

export type ButtonProps = BaseButtonProps &
  ({ asChild: true; loading?: never } | { asChild?: false; loading?: boolean });

const ButtonNestingContext = createContext(false);

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  type = 'button',
  disabled,
  children,
  onClick,
  ...props
}: ButtonProps) {
  const isNested = use(ButtonNestingContext);
  const isDisabled = loading || disabled;
  const Comp: 'button' | typeof Slot | 'span' = (() => {
    if (asChild) {
      return Slot;
    }

    if (isNested) {
      return 'span';
    }

    return 'button';
  })();

  const handleClick = isNested
    ? (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (!isDisabled) onClick?.(e);
      }
    : onClick;

  const buttonProps = {
    ...(isNested
      ? { 'aria-disabled': isDisabled || undefined }
      : {
          disabled: isDisabled,
        }),
    ...(isNested && !asChild ? {} : { type }),
    className: cn(buttonVariants({ variant, size, className })),
    'data-slot': 'button',
    onClick: handleClick,
    ...props,
  };

  return (
    <ButtonNestingContext value={true}>
      <Comp {...buttonProps}>
        {!asChild && loading ? (
          <Loader2Icon className="animate-spin" />
        ) : (
          children
        )}
      </Comp>
    </ButtonNestingContext>
  );
}

export { Button, buttonVariants };
